<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import i18n from '@/i18n'
import { sharedHelper } from '@/utils'
import { organizationService } from '@/helpers/services/organization.service'
import { proposalService } from '@/helpers/services/proposal.service'

const LAST_BRAND_KEY = 'proposals:lastTenantId'

/**
 * Briefs the bot collected for one brand, newest first.
 *
 * A brief becomes a proposal on the detail page. This list only answers
 * "what came in, and has anyone dealt with it" — the lead status comes from
 * ai_bot (captured / assigned / followed_up / closed).
 */
export default {
  components: { Layout, PageHeader },
  page() {
    return { title: i18n.t('module.proposal') }
  },
  data() {
    return {
      brands: [],
      tenantId: null,
      rows: [],
      loading: false,
      error: '',
      /** Client-side: one brand's briefs is a short list, and the filter has
       *  to feel instant while someone is scanning for "the one from Petronas". */
      statusFilter: '',
      proposalFilter: '',
      search: '',
    }
  },
  computed: {
    breadcrumbItems() {
      return [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.proposal'), active: true },
      ]
    },
    brandOptions() {
      return this.brands.map((b) => ({ value: b.tenant_id, text: `${b.name} (#${b.tenant_id})` }))
    },
    fields() {
      return [
        { key: 'leadRef', label: i18n.t('proposal.ref') },
        { key: 'company', label: i18n.t('proposal.company') },
        { key: 'contact', label: i18n.t('proposal.contact') },
        { key: 'pax', label: i18n.t('proposal.pax'), class: 'text-right' },
        { key: 'programme', label: i18n.t('proposal.programme') },
        { key: 'createdAt', label: i18n.t('proposal.received') },
        { key: 'status', label: i18n.t('label.status') },
        { key: 'proposal', label: i18n.t('proposal.proposalCol') },
        { key: 'open', label: '' },
      ]
    },
    tableRows() {
      return this.rows.map((r) => {
        const f = r.fields || {}
        return {
          leadRef: r.leadRef,
          company: f.company || '—',
          contact: [f.name, f.email].filter(Boolean).join(' · '),
          pax: f.pax || '',
          programme: f.programme_interest || '',
          createdAt: r.createdAt ? String(r.createdAt).replace('T', ' ').slice(0, 16) : '',
          status: r.status,
          proposal: r.proposal ? r.proposal.status : '',
          proposalVersion: r.proposal ? r.proposal.version : null,
        }
      })
    },
    visibleRows() {
      const q = this.search.trim().toLowerCase()
      return this.tableRows.filter((r) => {
        if (this.statusFilter && r.status !== this.statusFilter) return false
        if (this.proposalFilter === 'none' && r.proposal) return false
        if (this.proposalFilter && this.proposalFilter !== 'none' && r.proposal !== this.proposalFilter) return false
        if (q && ![r.leadRef, r.company, r.contact, r.programme].join(' ').toLowerCase().includes(q)) return false
        return true
      })
    },
    statusOptions() {
      return [
        { value: '', text: i18n.t('proposal.allStatuses') },
        ...['captured', 'assigned', 'followed_up', 'closed'].map((s) => ({ value: s, text: s })),
      ]
    },
    proposalOptions() {
      return [
        { value: '', text: i18n.t('proposal.allStatuses') },
        { value: 'none', text: i18n.t('proposal.noProposal') },
        ...['draft', 'approved', 'sent'].map((s) => ({ value: s, text: s })),
      ]
    },
  },
  async created() {
    await this.loadBrands()
    if (this.tenantId) await this.loadRows()
  },
  methods: {
    async loadBrands() {
      try {
        const res = await organizationService.getAllOrganizations({ pageNumber: 1, pageSize: 100 })
        this.brands = (res && res.data) || []
        let last = null
        try { last = Number(localStorage.getItem(LAST_BRAND_KEY)) || null } catch (e) { last = null }
        const ids = this.brands.map((b) => b.tenant_id)
        this.tenantId = ids.includes(last) ? last : (ids[0] || null)
      } catch (e) {
        this.error = (e && e.message) || String(e)
      }
    },
    async loadRows() {
      if (!this.tenantId) return
      this.loading = true
      this.error = ''
      try {
        try { localStorage.setItem(LAST_BRAND_KEY, String(this.tenantId)) } catch (e) { /* storage may be unavailable */ }
        const res = await proposalService.listBriefs(this.tenantId)
        this.rows = (res && res.data) || []
      } catch (e) {
        this.rows = []
        this.error = (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || String(e)
        sharedHelper.alertErrorMessageWithoutCode(this.error)
      } finally {
        this.loading = false
      }
    },
    statusVariant(s) {
      return { captured: 'warning', assigned: 'info', followed_up: 'primary', closed: 'secondary' }[s] || 'light'
    },
    proposalVariant(s) {
      return { draft: 'warning', approved: 'info', sent: 'success' }[s] || 'light'
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="$t('module.proposal')" :items="breadcrumbItems" />
    <div class="card">
      <div class="card-body">
        <div class="d-flex flex-wrap align-items-end justify-content-between mb-3" style="gap:12px">
          <b-form-group :label="$t('proposal.brand')" class="mb-0" style="min-width:320px">
            <b-form-select v-model="tenantId" :options="brandOptions" @change="loadRows" />
          </b-form-group>
          <small class="text-muted">{{ $t('proposal.listHint') }}</small>
        </div>
        <div class="d-flex flex-wrap align-items-end mb-3" style="gap:12px">
          <b-form-group :label="$t('proposal.filterStatus')" class="mb-0" style="min-width:180px">
            <b-form-select v-model="statusFilter" :options="statusOptions" size="sm" />
          </b-form-group>
          <b-form-group :label="$t('proposal.filterProposal')" class="mb-0" style="min-width:180px">
            <b-form-select v-model="proposalFilter" :options="proposalOptions" size="sm" />
          </b-form-group>
          <b-form-group :label="$t('proposal.search')" class="mb-0 flex-grow-1" style="min-width:240px">
            <b-form-input v-model="search" size="sm" :placeholder="$t('proposal.searchPlaceholder')" debounce="150" />
          </b-form-group>
          <small class="text-muted pb-1">{{ visibleRows.length }} / {{ tableRows.length }}</small>
        </div>
        <b-table :items="visibleRows" :fields="fields" :busy="loading" responsive="sm" show-empty hover>
          <template #empty><div class="text-muted small py-3">{{ tableRows.length ? $t('proposal.noMatch') : $t('proposal.empty') }}</div></template>
          <template #cell(leadRef)="d"><code>{{ d.value }}</code></template>
          <template #cell(status)="d"><b-badge :variant="statusVariant(d.value)">{{ d.value }}</b-badge></template>
          <template #cell(proposal)="d">
            <b-badge v-if="d.value" :variant="proposalVariant(d.value)">v{{ d.item.proposalVersion }} {{ d.value }}</b-badge>
            <span v-else class="text-muted small">—</span>
          </template>
          <template #cell(open)="d">
            <router-link :to="`/proposals/${d.item.leadRef}`"><b-button size="sm" variant="primary">{{ $t('proposal.open') }}</b-button></router-link>
          </template>
        </b-table>
      </div>
    </div>
  </Layout>
</template>
